// Pure, coverage-testable 3-D Explorer data transforms — Story #5116 Stream
// 8d. jsdom has no WebGL (vitest.config.ts's own note, same rationale as
// lib/whitespace-map.ts), so every piece of the projection-response ->
// scene-geometry math that the coverage floors (68/61/60/70) need to see
// lives HERE, in a THREE-free, DOM-free module. Explorer3D.tsx stays a thin
// WebGL render layer that feeds this module's plain-number output straight
// into THREE.BufferGeometry attributes — it is verified by the real-browser
// Playwright smoke test (e2e/explorer3d-scene.spec.ts), not by vitest.
//
// DENSITY FOG, NEVER RAW POINTS (Story #5116 design's hard rule, same
// discipline as LandscapeMap's 2-D density underlay, whitespace-map.ts's
// binPointsForDensity): the corpus is never rendered as one point per
// record — it is binned into a 3-D voxel grid first, and only the BINS
// become the fog's Points geometry. A corpus of thousands of records
// renders as at most gridSize^3 soft, density-coloured blobs, never a raw
// per-record scatter. The inventor's own project documents are the
// exception — always rendered as distinct markers, never folded into the
// fog (same as LandscapeMap's gold "Your idea" stars).
import type {
  WhitespaceMap3DDates,
  WhitespaceMap3DLayout,
  WhitespaceRegion,
  WhitespaceCompetitorRegion,
  ShellMesh,
  ProjectShells,
} from '../types/whitespace'
import { projectTagColor } from './whitespace'

export type Point3DKind = 'project' | 'neighbourhood'

export interface Point3D {
  id: string
  x: number
  y: number
  z: number
  kind: Point3DKind
  /** YYYYMMDD, neighbourhood points only — undefined for a project point or
   * a neighbourhood point the backend could not resolve a date for. */
  date?: number
}

const PROJECT_PREFIX = 'project:'

/** Turn a raw {record_id: [x, y, z]} layout (+ optional per-id dates) into
 * the point list this module's other transforms consume. Pure mapping — no
 * fetch, no DOM, no THREE. */
export function layoutToPoints3D(layout: WhitespaceMap3DLayout, dates?: WhitespaceMap3DDates | null): Point3D[] {
  return Object.entries(layout).map(([id, [x, y, z]]) => {
    const isProject = id.startsWith(PROJECT_PREFIX)
    return {
      id,
      x,
      y,
      z,
      kind: isProject ? 'project' : 'neighbourhood',
      date: isProject ? undefined : dates?.[id],
    }
  })
}

// A degenerate extent (0-1 points, or every point identical) still needs a
// span to scale/frame against — same documented arbitrary-but-safe unit as
// whitespace-map.ts's MIN_SPAN, never a divide-by-zero.
const MIN_SPAN = 1e-6

/** Floor for the Opacity channel. A voxel whose assigned attribute reads 0
 * must still be visible — fading it to literally invisible would hide real
 * data and read as a rendering bug rather than a low value. */
const OPACITY_MIN = 0.15

export interface Bounds3D {
  center: [number, number, number]
  /** Half the longest axis span, floored at MIN_SPAN — what Explorer3D.tsx
   * uses to place the camera far enough back to frame every point. */
  radius: number
}

/** Bounding sphere (centre + framing radius) over every point — project AND
 * neighbourhood together, since the camera must frame the whole scene. */
export function computeBounds3D(points: { x: number; y: number; z: number }[]): Bounds3D {
  if (points.length === 0) return { center: [0, 0, 0], radius: 1 }
  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  const zs = points.map((p) => p.z)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const minZ = Math.min(...zs)
  const maxZ = Math.max(...zs)
  const center: [number, number, number] = [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2]
  const radius = Math.max((maxX - minX) / 2, (maxY - minY) / 2, (maxZ - minZ) / 2, MIN_SPAN)
  return { center, radius }
}

export interface DensityVoxel {
  cx: number
  cy: number
  cz: number
  count: number
  /** Grid-cell address (col:row:lay) — a stable identity for this voxel
   * WITHIN one binning call, independent of the physical (cx, cy, cz) it
   * happens to land at. The dimension rig (Task #5156) uses this address,
   * not the position, as the animation key when a channel assignment
   * changes the coordinates being binned — the same grid cell keeps its
   * visual identity across the transition even though what occupies x/y/z
   * has changed, which is what makes the re-layout identity-preserving
   * without falling back to per-record raw points (the density-fog rule
   * above is never per-record identity, it is per-bin). */
  col: number
  row: number
  lay: number
  /** ids of every point binned into this voxel — used by the dimension
   * rig (Task #5156) both to average a colour/size channel's attribute
   * value across a voxel's own members and to scope semantic-zoom
   * drill-in to "the records in the cluster that was double-clicked". */
  recordIds: string[]
}

const DEFAULT_GRID_SIZE = 10
const DEFAULT_PADDING_RATIO = 0.15

/** Bin points into a `gridSize`^3 voxel grid — the 3-D counterpart of
 * whitespace-map.ts's binPointsForDensity. Only occupied voxels are
 * returned (never a dense gridSize^3 array of mostly-zero cells). */
export function binPointsForDensity3D(
  points: { id?: string; x: number; y: number; z: number }[],
  gridSize = DEFAULT_GRID_SIZE,
  paddingRatio = DEFAULT_PADDING_RATIO,
): DensityVoxel[] {
  if (points.length === 0) return []

  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  const zs = points.map((p) => p.z)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const minZ = Math.min(...zs)
  const maxZ = Math.max(...zs)

  const spanX = Math.max(maxX - minX, MIN_SPAN) * (1 + 2 * paddingRatio)
  const spanY = Math.max(maxY - minY, MIN_SPAN) * (1 + 2 * paddingRatio)
  const spanZ = Math.max(maxZ - minZ, MIN_SPAN) * (1 + 2 * paddingRatio)
  const originX = (minX + maxX) / 2 - spanX / 2
  const originY = (minY + maxY) / 2 - spanY / 2
  const originZ = (minZ + maxZ) / 2 - spanZ / 2
  const cellW = spanX / gridSize
  const cellH = spanY / gridSize
  const cellD = spanZ / gridSize

  const counts = new Map<string, number>()
  const memberIds = new Map<string, string[]>()
  for (const p of points) {
    const col = Math.min(gridSize - 1, Math.max(0, Math.floor((p.x - originX) / cellW)))
    const row = Math.min(gridSize - 1, Math.max(0, Math.floor((p.y - originY) / cellH)))
    const lay = Math.min(gridSize - 1, Math.max(0, Math.floor((p.z - originZ) / cellD)))
    const key = `${col}:${row}:${lay}`
    counts.set(key, (counts.get(key) ?? 0) + 1)
    if (p.id) {
      const ids = memberIds.get(key)
      if (ids) ids.push(p.id)
      else memberIds.set(key, [p.id])
    }
  }

  const voxels: DensityVoxel[] = []
  for (const [key, count] of counts) {
    const [colStr, rowStr, layStr] = key.split(':')
    const col = Number(colStr)
    const row = Number(rowStr)
    const lay = Number(layStr)
    voxels.push({
      cx: originX + (col + 0.5) * cellW,
      cy: originY + (row + 0.5) * cellH,
      cz: originZ + (lay + 0.5) * cellD,
      count,
      col,
      row,
      lay,
      recordIds: memberIds.get(key) ?? [],
    })
  }
  return voxels
}

/** Stable animation key for a density voxel — the grid-cell address, never
 * the position (see DensityVoxel.col's own doc for why). */
export function voxelKey(v: Pick<DensityVoxel, 'col' | 'row' | 'lay'>): string {
  return `fog:${v.col}:${v.row}:${v.lay}`
}

// ---------------------------------------------------------------------------
// Scene geometry — the exact plain-number arrays Explorer3D.tsx feeds into
// THREE.BufferGeometry attributes (wrapped in Float32Array at the render
// boundary). Kept as plain arrays here so this module stays THREE-free and
// exercisable in jsdom.
// ---------------------------------------------------------------------------

// Density colour ramp — app theme only (CLAUDE.md "Generated Visuals…
// ALWAYS App Theme, NEVER Dark"): a faint, pale sky tint at low density
// deepening toward the app's own secondary sky-blue (#0284C7) at peak
// density — never a dark/black fog.
const FOG_COLOR_LOW: [number, number, number] = [0xba / 255, 0xe6 / 255, 0xfd / 255] // pale sky (#BAE6FD)
const FOG_COLOR_HIGH: [number, number, number] = [0x02 / 255, 0x84 / 255, 0xc7 / 255] // app secondary (#0284C7)

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** The same low-density -> high-density app-theme colour ramp
 * buildFogSceneDataFromPoints/buildRigSceneData use, exposed standalone so
 * Explorer3D.tsx can colour an individual full-fidelity point during
 * semantic-zoom drill-in (never binned, so it needs its own colour, not a
 * voxel average) along the identical ramp. */
export function colorRampAt(t: number): [number, number, number] {
  const ct = Math.max(0, Math.min(1, t))
  return [
    lerp(FOG_COLOR_LOW[0], FOG_COLOR_HIGH[0], ct),
    lerp(FOG_COLOR_LOW[1], FOG_COLOR_HIGH[1], ct),
    lerp(FOG_COLOR_LOW[2], FOG_COLOR_HIGH[2], ct),
  ]
}

export interface FogSceneData {
  /** Flat [x,y,z, x,y,z, …] for every occupied density voxel — the fog. */
  fogPositions: number[]
  /** Flat [r,g,b, r,g,b, …] per voxel, density-encoded along the app-theme
   * colour ramp — never a raw per-record colour, always a bin's density. */
  fogColors: number[]
  /** Normalised 0..1 density per voxel (count / max count) — plain data,
   * kept separate from fogColors so the colour ramp itself is independently
   * testable and this array can drive point size/opacity too. */
  fogDensities: number[]
  /** voxelKey() per voxel, SAME order as fogPositions/fogColors/fogDensities
   * — the stable per-bin identity the dimension rig's animated re-layout
   * (Task #5156, see lerpKeyedScene) keys transitions on. */
  fogKeys: string[]
  /** Flat [x,y,z, …] for the inventor's OWN documents — rendered as
   * distinct markers on top of the fog, never folded into it. */
  projectPositions: number[]
  /** record id per project marker, SAME order as projectPositions — a
   * project marker's own id IS its stable animation identity (it is never
   * binned, so no separate grid-address key is needed). */
  projectIds: string[]
  center: [number, number, number]
  radius: number
  /** True when there is nothing to render at all (no layout yet). */
  isEmpty: boolean
}

/** Pure transform: an already-built Point3D list -> the density-fog scene
 * geometry Explorer3D.tsx renders. Shared by buildFogSceneData (the raw
 * layout entry point) and the dimension rig (Task #5156), which re-derives
 * the point list from a channel assignment before binning — see
 * applyChannelAssignment. */
export function buildFogSceneDataFromPoints(points: Point3D[], gridSize = DEFAULT_GRID_SIZE): FogSceneData {
  if (points.length === 0) {
    return {
      fogPositions: [],
      fogColors: [],
      fogDensities: [],
      fogKeys: [],
      projectPositions: [],
      projectIds: [],
      center: [0, 0, 0],
      radius: 1,
      isEmpty: true,
    }
  }

  const neighbourhoodPoints = points.filter((p) => p.kind === 'neighbourhood')
  const projectPoints = points.filter((p) => p.kind === 'project')
  const { center, radius } = computeBounds3D(points)
  const voxels = binPointsForDensity3D(neighbourhoodPoints, gridSize)
  const maxCount = Math.max(1, ...voxels.map((v) => v.count))

  const fogPositions: number[] = []
  const fogColors: number[] = []
  const fogDensities: number[] = []
  const fogKeys: string[] = []
  for (const v of voxels) {
    fogPositions.push(v.cx, v.cy, v.cz)
    const density = v.count / maxCount
    fogDensities.push(density)
    fogColors.push(
      lerp(FOG_COLOR_LOW[0], FOG_COLOR_HIGH[0], density),
      lerp(FOG_COLOR_LOW[1], FOG_COLOR_HIGH[1], density),
      lerp(FOG_COLOR_LOW[2], FOG_COLOR_HIGH[2], density),
    )
    fogKeys.push(voxelKey(v))
  }

  const projectPositions: number[] = []
  const projectIds: string[] = []
  for (const p of projectPoints) {
    projectPositions.push(p.x, p.y, p.z)
    projectIds.push(p.id)
  }

  return {
    fogPositions,
    fogColors,
    fogDensities,
    fogKeys,
    projectPositions,
    projectIds,
    center,
    radius,
    isEmpty: fogPositions.length === 0 && projectPositions.length === 0,
  }
}

/** Pure transform: a raw {record_id: [x,y,z]} 3-D projection response ->
 * the density-fog scene geometry Explorer3D.tsx renders. No THREE import,
 * no DOM — this is the function vitest's coverage floors exercise
 * directly, since jsdom cannot exercise the WebGL render itself. */
export function buildFogSceneData(
  layout: WhitespaceMap3DLayout | null | undefined,
  dates?: WhitespaceMap3DDates | null,
  gridSize = DEFAULT_GRID_SIZE,
): FogSceneData {
  return buildFogSceneDataFromPoints(layoutToPoints3D(layout ?? {}, dates), gridSize)
}

// ---------------------------------------------------------------------------
// Dimension rig — Story #5116 Stream 8e (Task #5156). Pure, THREE-free
// attribute/channel data layer: which attribute values a record carries,
// how a channel assignment re-derives point coordinates/colour/size from
// them, and the keyed interpolation Explorer3D.tsx's render loop uses for
// an identity-preserving (never a hard cut) animated transition.
// ---------------------------------------------------------------------------

/** The four attributes the dimension rig's shelf offers. `filing_momentum`
 * is region.momentum under an ACCURATE label — Task #5151/Stream 8c shipped
 * the market-interest composite as a pure passthrough of the existing WOS
 * momentum sub-score (100% patent-filing-derived, scoring.py's
 * MARKET_INTEREST_AVAILABLE_SIGNALS = ['filing_momentum']) and it is not
 * wired into any route of its own — so the frontend reads region.momentum
 * directly and must show it under the corrected label, never
 * "journal-momentum" or bare "momentum". */
export type AttributeKey = 'wos_score' | 'filing_momentum' | 'competitor_proximity' | 'enablement'

export interface AttributeDefinition {
  key: AttributeKey
  /** Exact chip/label text. Design §5.1 (Story #5305, "one metric, three
   * names" — the same market-interest number was "Filing momentum ratio"
   * here, "Momentum" on the Explorer's cards, and `filing_momentum` in the
   * raw payload): "Filing momentum" is now the ONE name shown everywhere;
   * the payload alias moves into `payloadAlias` below, quoted only inside
   * this chip's own popover, never as the visible label. */
  label: string
  /** The raw field name this attribute reads off the wire — surfaced only
   * in the attribute's popover ("also called filing_momentum in the raw
   * data"), never as user-facing label text. Optional: only attributes with
   * a payload name that differs from their display label carry one. */
  payloadAlias?: string
  /** One sentence, plain language: what this attribute measures and where
   * it comes from — the dimension rig's own popover content (design §5.7:
   * "each channel slot... a plain-language popover"). */
  description: string
  /** Fixed value domain used to normalise into 0..1 — a documented
   * constant, not a per-dataset min/max (same discipline as this file's own
   * MIN_SPAN and whitespace-map.ts's: an honest fixed unit beats a range
   * that would make the same score plot differently project to project). */
  domain: [number, number]
}

export const ATTRIBUTE_DEFINITIONS: AttributeDefinition[] = [
  {
    key: 'wos_score',
    label: 'WOS score',
    description: 'This region\'s overall White-Opportunity Score (0–100) — the same composite ranking the Opportunity Explorer cards use.',
    domain: [0, 100],
  },
  {
    key: 'filing_momentum',
    label: 'Filing momentum',
    payloadAlias: 'filing_momentum',
    description: 'How fast patent filings in this neighbourhood have been arriving recently, 0–100 — a market-interest signal derived entirely from filing dates, never from journal activity.',
    domain: [0, 100],
  },
  {
    key: 'competitor_proximity',
    label: 'Competitor proximity',
    description: 'How close the nearest identified competitor cluster sits to this point, 0 (far) – 1 (overlapping).',
    domain: [0, 1],
  },
  {
    // design §5.1 "one metric gets one name" — this reads region.enablement
    // directly (line below), the same field the Explorer card v2 and its
    // detail view show under "Feasibility signal" (Task #5328's fix) —
    // never the raw internal metric name.
    key: 'enablement',
    label: 'Feasibility signal',
    description: 'How much of the region is backed by enabling disclosure rather than bare claims, 0–100.',
    domain: [0, 100],
  },
]

export type DimensionChannel = 'x' | 'y' | 'z' | 'color' | 'size' | 'opacity'

export const DIMENSION_CHANNELS: { channel: DimensionChannel; label: string }[] = [
  { channel: 'x', label: 'X axis' },
  { channel: 'y', label: 'Y axis' },
  { channel: 'z', label: 'Z axis' },
  { channel: 'color', label: 'Colour' },
  { channel: 'size', label: 'Size' },
  { channel: 'opacity', label: 'Opacity' },
]

/** Which attribute (if any) is currently assigned to each channel. An
 * unassigned channel keeps its existing behaviour: x/y/z keep the record's
 * own projected coordinate, colour/size keep the density-based default. */
export type ChannelAssignment = Partial<Record<DimensionChannel, AttributeKey>>

/** First-run axis mapping for the single-project 3-D Explorer (UX review
 * Finding 6). Without it the Explorer opened with "0 of 3 axes mapped" and a
 * bare point cloud, which read as unbuilt. The three spatial axes map to the
 * three attributes that are ALWAYS populated from region data — wos_score,
 * filing_momentum (region.momentum) and enablement — so no axis ever collapses
 * (competitor_proximity is deliberately excluded: it depends on competitor data
 * that can be absent). Colour/size/opacity keep their density-based defaults,
 * and the viewer can re-map any channel freely from the Dimension Rig. Applied
 * ONLY in single-project mode; the Portfolio Atlas 3-D (shell mode) keeps the
 * embedding-projection default. */
export const DEFAULT_CHANNEL_ASSIGNMENT: ChannelAssignment = {
  x: 'wos_score',
  y: 'filing_momentum',
  z: 'enablement',
}

export interface RecordAttributes {
  wos_score?: number
  filing_momentum?: number
  enablement?: number
  /** Undefined when the record's region had no recognised competitor in
   * band — an honest gap, never a fabricated 0 (see
   * whitespace/detectors.py's nearest_competitor_by_region docstring). */
  competitor_proximity?: number
}

function bandKeyOf(band: [number, number]): string {
  return `${band[0]}-${band[1]}`
}

/** Join a project's scored WOS regions (+ optional competitor-proximity
 * regions, same band edges) into a per-record-id attribute lookup, using
 * each region's own patent_record_ids/academic_record_ids — the SAME
 * id-to-region membership convention whitespace-map.ts's
 * computeRegionOutlines uses for the 2-D map. A record whose id appears in
 * no region carries no entry (an honest gap: the dimension rig falls back
 * to the record's own projected coordinate / the density default for that
 * point, never a fabricated value). */
export function buildRecordAttributeIndex(
  regions: WhitespaceRegion[],
  competitorRegions?: WhitespaceCompetitorRegion[] | null,
): Map<string, RecordAttributes> {
  const proximityByBand = new Map<string, number>()
  for (const cr of competitorRegions ?? []) {
    if (cr.proximity_cosine != null) proximityByBand.set(bandKeyOf(cr.band), cr.proximity_cosine)
  }

  const index = new Map<string, RecordAttributes>()
  for (const region of regions) {
    const attrs: RecordAttributes = {
      wos_score: region.wos_score,
      filing_momentum: region.momentum,
      enablement: region.enablement,
      competitor_proximity: proximityByBand.get(bandKeyOf(region.band)),
    }
    for (const id of [...region.patent_record_ids, ...region.academic_record_ids]) {
      index.set(id, attrs)
    }
  }
  return index
}

/** Normalise a raw attribute value into 0..1 against its fixed domain,
 * clamped at both ends (a value outside its documented domain is graphed at
 * the domain edge, never allowed to invert or overflow the scene). */
export function normalizeAttributeT(key: AttributeKey, value: number): number {
  const def = ATTRIBUTE_DEFINITIONS.find((d) => d.key === key)
  if (!def) return 0.5
  const [lo, hi] = def.domain
  if (hi === lo) return 0.5
  return Math.max(0, Math.min(1, (value - lo) / (hi - lo)))
}

// Spatial span an attribute-substituted axis is normalised into — an
// arbitrary-but-documented data-space unit, same convention as this file's
// own MIN_SPAN, chosen so an attribute-driven axis composes reasonably with
// an unassigned axis that still carries the original PCA-projected range.
const SPATIAL_ATTRIBUTE_RANGE = 10

/** Re-derive each point's x/y/z from `assignment`'s spatial channels — an
 * axis with no attribute assigned keeps the point's own projected
 * coordinate untouched. A point with no attribute data for an assigned
 * axis ALSO keeps its own coordinate on that axis (honest gap, never
 * collapsed to 0). Pure — this is what Explorer3D.tsx re-runs, together
 * with buildFogSceneDataFromPoints, whenever the rig's channel assignment
 * changes. */
export function applyChannelAssignment(
  points: Point3D[],
  attributesById: Map<string, RecordAttributes>,
  assignment: ChannelAssignment,
): Point3D[] {
  if (!assignment.x && !assignment.y && !assignment.z) return points

  const spatialAxes: ('x' | 'y' | 'z')[] = ['x', 'y', 'z']
  return points.map((p) => {
    const attrs = attributesById.get(p.id)
    let { x, y, z } = p
    for (const axis of spatialAxes) {
      const key = assignment[axis]
      if (!key) continue
      const raw = attrs?.[key]
      if (raw === undefined) continue
      const t = normalizeAttributeT(key, raw)
      const coord = (t - 0.5) * SPATIAL_ATTRIBUTE_RANGE
      if (axis === 'x') x = coord
      else if (axis === 'y') y = coord
      else z = coord
    }
    return { ...p, x, y, z }
  })
}

/** Average an attribute's normalised 0..1 value across a voxel's own member
 * record ids — undefined when none of the voxel's members carry that
 * attribute (honest gap; the caller falls back to the density-based
 * default rather than inventing a mid-point). */
export function averageAttributeT(
  recordIds: string[],
  attributesById: Map<string, RecordAttributes>,
  key: AttributeKey,
): number | undefined {
  const values: number[] = []
  for (const id of recordIds) {
    const raw = attributesById.get(id)?.[key]
    if (raw !== undefined) values.push(normalizeAttributeT(key, raw))
  }
  if (values.length === 0) return undefined
  return values.reduce((sum, v) => sum + v, 0) / values.length
}

/** buildFogSceneDataFromPoints's dimension-rig-aware counterpart: applies
 * the channel assignment's spatial axes before binning, and — when colour
 * and/or size is assigned to an attribute — colours/sizes each voxel by
 * the ASSIGNED attribute's own average across that voxel's members instead
 * of density. A voxel with no member carrying the assigned attribute falls
 * back to the density-based value for that channel (never a fabricated
 * reading), so `fogColors`/`fogSizes` are always fully populated. */
export function buildRigSceneData(
  points: Point3D[],
  attributesById: Map<string, RecordAttributes>,
  assignment: ChannelAssignment,
  gridSize = DEFAULT_GRID_SIZE,
): FogSceneData & { fogSizes: number[]; fogOpacities: number[]; voxels: DensityVoxel[] } {
  const positioned = applyChannelAssignment(points, attributesById, assignment)
  if (positioned.length === 0) {
    return { ...buildFogSceneDataFromPoints(positioned, gridSize), fogSizes: [], fogOpacities: [], voxels: [] }
  }

  const neighbourhoodPoints = positioned.filter((p) => p.kind === 'neighbourhood')
  const projectPoints = positioned.filter((p) => p.kind === 'project')
  const { center, radius } = computeBounds3D(positioned)
  const voxels = binPointsForDensity3D(neighbourhoodPoints, gridSize)
  const maxCount = Math.max(1, ...voxels.map((v) => v.count))

  const colorKey = assignment.color
  const sizeKey = assignment.size
  const opacityKey = assignment.opacity

  const fogPositions: number[] = []
  const fogColors: number[] = []
  const fogDensities: number[] = []
  const fogSizes: number[] = []
  const fogOpacities: number[] = []
  const fogKeys: string[] = []
  for (const v of voxels) {
    fogPositions.push(v.cx, v.cy, v.cz)
    const density = v.count / maxCount
    fogDensities.push(density)

    const colorT = (colorKey && averageAttributeT(v.recordIds, attributesById, colorKey)) ?? density
    fogColors.push(
      lerp(FOG_COLOR_LOW[0], FOG_COLOR_HIGH[0], colorT),
      lerp(FOG_COLOR_LOW[1], FOG_COLOR_HIGH[1], colorT),
      lerp(FOG_COLOR_LOW[2], FOG_COLOR_HIGH[2], colorT),
    )

    const sizeT = (sizeKey && averageAttributeT(v.recordIds, attributesById, sizeKey)) ?? 0.5
    fogSizes.push(sizeT)

    // Unassigned Opacity keeps every voxel fully opaque, so the scene is
    // pixel-identical to its pre-channel self; assigned, it maps the
    // attribute onto OPACITY_MIN..1 — never to 0, which would silently
    // delete a real voxel from the reading rather than de-emphasise it.
    const opacityT = opacityKey ? averageAttributeT(v.recordIds, attributesById, opacityKey) : undefined
    fogOpacities.push(opacityT === undefined ? 1 : OPACITY_MIN + (1 - OPACITY_MIN) * opacityT)

    fogKeys.push(voxelKey(v))
  }

  const projectPositions: number[] = []
  const projectIds: string[] = []
  for (const p of projectPoints) {
    projectPositions.push(p.x, p.y, p.z)
    projectIds.push(p.id)
  }

  return {
    fogPositions,
    fogColors,
    fogDensities,
    fogSizes,
    fogOpacities,
    fogKeys,
    projectPositions,
    projectIds,
    center,
    radius,
    isEmpty: fogPositions.length === 0 && projectPositions.length === 0,
    voxels,
  }
}

// ---------------------------------------------------------------------------
// Identity-preserving animated transitions — keyed by voxelKey()/record id,
// never by array index (an array index shifts when the voxel count changes
// between two channel assignments, which is exactly the "hard cut" this
// task's own Steps rule out).
// ---------------------------------------------------------------------------

export interface SceneKeyedPoint {
  key: string
  x: number
  y: number
  z: number
  r: number
  g: number
  b: number
  /** 0..1 fade multiplier applied to point size/opacity — 1 for a point
   * present in both the previous and next scene, animating from/to 0 for a
   * point that is only appearing or only disappearing this transition. */
  scale: number
  /** 0..1 per-point alpha multiplier from the rig's Opacity channel. 1 when
   * Opacity is unassigned, which reproduces the pre-channel behaviour exactly
   * (the material's single uOpacity uniform then decides on its own). */
  alpha: number
}

/** Interpolate between two keyed scene snapshots at t (0..1), matching
 * entries by `key`: a key present in both `from` and `to` moves/recolours
 * smoothly between them; a key only in `to` fades in (scale 0 -> its own
 * target scale) at its TARGET position; a key only in `from` fades out
 * (scale -> 0) holding its OLD position — never an instant appear/disappear
 * or a snap to a new position. Pure, THREE-free: Explorer3D.tsx's render
 * loop calls this once per animated frame and writes the result straight
 * into BufferGeometry attributes. */
export function lerpKeyedScene(
  from: Map<string, SceneKeyedPoint>,
  to: Map<string, SceneKeyedPoint>,
  t: number,
): SceneKeyedPoint[] {
  const ct = Math.max(0, Math.min(1, t))
  const keys = new Set<string>([...from.keys(), ...to.keys()])
  const out: SceneKeyedPoint[] = []
  for (const key of keys) {
    const a = from.get(key)
    const b = to.get(key)
    if (a && b) {
      out.push({
        key,
        x: lerp(a.x, b.x, ct),
        y: lerp(a.y, b.y, ct),
        z: lerp(a.z, b.z, ct),
        r: lerp(a.r, b.r, ct),
        g: lerp(a.g, b.g, ct),
        b: lerp(a.b, b.b, ct),
        scale: lerp(a.scale, b.scale, ct),
        alpha: lerp(a.alpha, b.alpha, ct),
      })
    } else if (b) {
      out.push({ ...b, scale: b.scale * ct })
    } else if (a) {
      out.push({ ...a, scale: a.scale * (1 - ct) })
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Semantic-zoom drill-in — a double-clicked voxel's own member record ids
// scope the next render to "that local neighbourhood at full fidelity"
// (individual points, not re-binned) — no server-side scoping parameter
// exists on Task #5149's /3d route (it always projects the whole cached
// neighbourhood), so the scoping is this bounded client-side filter over
// the SAME already-fetched-and-permission-checked layout; a fresh GET
// against the real route (Explorer3D.tsx's refetch) still backs the drilled
// view with a live re-invoke, never a synthetic fixture.
// ---------------------------------------------------------------------------

export interface DrillCrumb {
  /** Breadcrumb label — "Full corpus" for the unscoped root, else a short
   * description of the drilled cluster (record count). */
  label: string
  /** Record ids this level is scoped to; null for the unscoped root. */
  recordIds: string[] | null
}

export const ROOT_DRILL_CRUMB: DrillCrumb = { label: 'Full corpus', recordIds: null }

/** Points scoped to `ids` — the drilled view's "full fidelity" render input
 * (individual points, not re-binned into fog). */
export function filterPointsByIds(points: Point3D[], ids: string[]): Point3D[] {
  const idSet = new Set(ids)
  return points.filter((p) => idSet.has(p.id))
}

// ---------------------------------------------------------------------------
// Keyed-scene assembly — moved here from Explorer3D.tsx (Story #5305 Task
// #5326 Stream 11b, Bug #5298) so this genuinely THREE-free, DOM-free
// computation can run identically on the main thread OR inside
// whitespace-3d.worker.ts. This was the "heavy layout math" a browser
// profiler caught blocking the main thread for long enough to time out
// screenshot captures roughly 1 in 3 runs, clustered right after a White
// Space sub-view transition and a dimension-rig channel reassignment — the
// two moments this module's `computeTargetScene` actually recomputes a
// scene from scratch. Explorer3D.tsx's retarget effect now posts to the
// worker instead of calling this on the main thread directly; the worker
// entry point re-exports these same functions so there is exactly one
// implementation, never a synced-by-hand main-thread/worker pair.
// ---------------------------------------------------------------------------

// App-theme colours only (CLAUDE.md "Generated Visuals… ALWAYS App Theme,
// NEVER Dark") — the amber CTA colour for the inventor's own project
// markers, same accent LandscapeMap.tsx uses for its gold "Your idea" stars.
export const PROJECT_MARKER_COLOR: [number, number, number] = [0xfb / 255, 0xbf / 255, 0x24 / 255] // amber CTA (#FBBF24)

// Per-vertex size multipliers (Explorer3D.tsx's custom ShaderMaterial reads
// these) — fog voxels read as soft, small blobs; a drilled individual
// record reads noticeably larger (this IS "full fidelity" — a real,
// addressable point, not a density bin); a project marker stays the largest
// and most distinct.
export const FOG_MIN_SCALE = 0.6
export const FOG_MAX_SCALE = 1.6
export const DRILL_MIN_SCALE = 1.4
export const DRILL_MAX_SCALE = 2.6
export const PROJECT_SCALE = 3.2

/** Drill-in counterpart of the fog view's OPACITY_MIN — a point whose
 * Opacity attribute reads 0 stays visible rather than vanishing from the
 * reading. */
export const DRILL_OPACITY_MIN = 0.15

export interface KeyedScene {
  map: Map<string, SceneKeyedPoint>
  center: [number, number, number]
  radius: number
  isEmpty: boolean
  /** fogKey -> that voxel's own member record ids — only populated at the
   * (unscoped) root level; empty once drilled in, since a drilled view has
   * no further voxels to drill into. */
  voxelLookup: Map<string, string[]>
}

/** The root (unscoped) reading: density fog + project markers, honouring
 * the current channel assignment — see buildRigSceneData for the actual
 * coordinate/colour/size math. */
export function buildRootKeyedScene(
  points: Point3D[],
  attributesById: Map<string, RecordAttributes>,
  assignment: ChannelAssignment,
): KeyedScene {
  const rig = buildRigSceneData(points, attributesById, assignment)
  const map = new Map<string, SceneKeyedPoint>()
  for (let i = 0; i < rig.fogKeys.length; i++) {
    const idx = i * 3
    map.set(rig.fogKeys[i], {
      key: rig.fogKeys[i],
      x: rig.fogPositions[idx],
      y: rig.fogPositions[idx + 1],
      z: rig.fogPositions[idx + 2],
      r: rig.fogColors[idx],
      g: rig.fogColors[idx + 1],
      b: rig.fogColors[idx + 2],
      scale: FOG_MIN_SCALE + (FOG_MAX_SCALE - FOG_MIN_SCALE) * rig.fogSizes[i],
      alpha: rig.fogOpacities[i] ?? 1,
    })
  }
  for (let i = 0; i < rig.projectIds.length; i++) {
    const idx = i * 3
    const key = `project:${rig.projectIds[i]}`
    map.set(key, {
      key,
      x: rig.projectPositions[idx],
      y: rig.projectPositions[idx + 1],
      z: rig.projectPositions[idx + 2],
      r: PROJECT_MARKER_COLOR[0],
      g: PROJECT_MARKER_COLOR[1],
      b: PROJECT_MARKER_COLOR[2],
      scale: PROJECT_SCALE,
      alpha: 1, // the user's own documents are never faded by a corpus attribute
    })
  }
  return {
    map,
    center: rig.center,
    radius: rig.radius,
    isEmpty: rig.isEmpty,
    voxelLookup: new Map(rig.voxels.map((v) => [voxelKey(v), v.recordIds])),
  }
}

/** The drilled-in reading: `scopedPoints` (already channel-assigned)
 * rendered individually — "full fidelity", never re-binned. */
export function buildDrilledKeyedScene(
  scopedPoints: Point3D[],
  attributesById: Map<string, RecordAttributes>,
  assignment: ChannelAssignment,
): KeyedScene {
  const map = new Map<string, SceneKeyedPoint>()
  for (const p of scopedPoints) {
    const attrs = attributesById.get(p.id)
    const isProject = p.kind === 'project'
    const colorKey = assignment.color
    const colorT = colorKey && attrs?.[colorKey] !== undefined ? normalizeAttributeT(colorKey, attrs[colorKey] as number) : 0.7
    const [r, g, b] = isProject ? PROJECT_MARKER_COLOR : colorRampAt(colorT)
    const sizeKey = assignment.size
    const sizeT = sizeKey && attrs?.[sizeKey] !== undefined ? normalizeAttributeT(sizeKey, attrs[sizeKey] as number) : 0.5
    const scale = isProject ? PROJECT_SCALE : DRILL_MIN_SCALE + (DRILL_MAX_SCALE - DRILL_MIN_SCALE) * sizeT
    const opacityKey = assignment.opacity
    const opacityT = opacityKey && attrs?.[opacityKey] !== undefined ? normalizeAttributeT(opacityKey, attrs[opacityKey] as number) : undefined
    const alpha = isProject || opacityT === undefined ? 1 : DRILL_OPACITY_MIN + (1 - DRILL_OPACITY_MIN) * opacityT
    const key = isProject ? `project:${p.id}` : `pt:${p.id}`
    map.set(key, { key, x: p.x, y: p.y, z: p.z, r, g, b, scale, alpha })
  }
  const bounds = computeBounds3D(scopedPoints.length ? scopedPoints : [{ x: 0, y: 0, z: 0 }])
  return { map, center: bounds.center, radius: bounds.radius, isEmpty: scopedPoints.length === 0, voxelLookup: new Map() }
}

/** Root or drilled reading, whichever `drillPath`'s active crumb calls for
 * — the single entry point both the main thread (initial mount) and
 * whitespace-3d.worker.ts (every retarget) call. */
export function computeTargetScene(
  points: Point3D[],
  attributesById: Map<string, RecordAttributes>,
  assignment: ChannelAssignment,
  drillPath: DrillCrumb[],
): KeyedScene {
  const activeCrumb = drillPath[drillPath.length - 1]
  if (!activeCrumb || !activeCrumb.recordIds) {
    return buildRootKeyedScene(points, attributesById, assignment)
  }
  const scoped = filterPointsByIds(points, activeCrumb.recordIds)
  const positioned = applyChannelAssignment(scoped, attributesById, assignment)
  return buildDrilledKeyedScene(positioned, attributesById, assignment)
}

// ---------------------------------------------------------------------------
// 3-D alpha-shape shells — Story #5305 Task #5339 Stream 15 (design §6.5/§7).
// Pure, THREE-free mesh-geometry transforms over whitespace/overlap_shells.py's
// /portfolio_shells response (types/whitespace.ts's ShellMesh/ProjectShells/
// ShellIntersection/PortfolioShellHole) — Explorer3D.tsx's overlap-mode
// render layer feeds this module's plain-number output straight into
// THREE.BufferGeometry attributes, same division of labour as the fog scene
// above. Never one Points-per-record: a shell is already just its own
// boundary triangles (overlap_shells.py's own "only vertices actually used
// by a boundary face are included" contract), so no additional binning is
// needed here.
// ---------------------------------------------------------------------------

/** '#RRGGBB' -> [r, g, b] each in 0..1, for a THREE vertex-color attribute.
 * Reuses `projectTagColor`'s own app-theme palette (lib/whitespace.ts) so a
 * project's colour is IDENTICAL between the 2-D Portfolio Atlas and this
 * 3-D shell rendering — never a second, independently-chosen palette. */
export function hexToRgb01(hex: string): [number, number, number] {
  const clean = hex.replace('#', '')
  const r = parseInt(clean.slice(0, 2), 16) / 255
  const g = parseInt(clean.slice(2, 4), 16) / 255
  const b = parseInt(clean.slice(4, 6), 16) / 255
  return [r, g, b]
}

export interface ShellTriangleGeometry {
  /** Flat [x,y,z, x,y,z, x,y,z, ...], 3 vertices per triangle — a
   * NON-INDEXED THREE.BufferGeometry (a shell mesh is small — a handful to
   * a few dozen triangles, never per-corpus-record — so the memory cost of
   * duplicating shared vertices per triangle is negligible, and it lets
   * every triangle carry its own colour without an index buffer). */
  positions: number[]
  /** Flat [r,g,b, ...], SAME length/order as positions/3 — one solid colour
   * per shell (the project's own tag colour), repeated per vertex so the
   * shader can read it as a per-vertex attribute like the fog scene's
   * aColor. */
  colors: number[]
}

/** A shell mesh -> the exact triangle-soup arrays Explorer3D.tsx's overlap
 * mode feeds into a THREE.BufferGeometry (`position` + a per-vertex colour
 * attribute for a translucent MeshBasicMaterial with vertexColors). */
export function shellMeshToTriangleGeometry(mesh: ShellMesh, color: [number, number, number]): ShellTriangleGeometry {
  const positions: number[] = []
  const colors: number[] = []
  for (const [a, b, c] of mesh.triangles) {
    for (const idx of [a, b, c]) {
      const v = mesh.vertices[idx]
      if (!v) continue
      positions.push(v[0], v[1], v[2])
      colors.push(color[0], color[1], color[2])
    }
  }
  return { positions, colors }
}

/** A shell mesh's own EDGES (each triangle's 3 sides, de-duplicated so a
 * shared edge between two triangles is drawn once) as a flat LineSegments
 * position array — the faceted wireframe outline Figure 6's shells show
 * over their translucent fill. */
export function shellMeshToWireframePositions(mesh: ShellMesh): number[] {
  const seen = new Set<string>()
  const positions: number[] = []
  const pushEdge = (i: number, j: number) => {
    const key = i < j ? `${i}:${j}` : `${j}:${i}`
    if (seen.has(key)) return
    seen.add(key)
    const p1 = mesh.vertices[i]
    const p2 = mesh.vertices[j]
    if (!p1 || !p2) return
    positions.push(p1[0], p1[1], p1[2], p2[0], p2[1], p2[2])
  }
  for (const [a, b, c] of mesh.triangles) {
    pushEdge(a, b)
    pushEdge(b, c)
    pushEdge(c, a)
  }
  return positions
}

/** Centroid of a mesh's own vertex set — used to anchor the shell's
 * floating project-name label (Figure 6's "Marine Sensor Mesh — tight
 * shell" callouts). Null for an empty/absent mesh — never a fabricated
 * [0,0,0] that would silently mislabel the scene's real origin. */
export function shellCentroid(mesh: ShellMesh | null | undefined): [number, number, number] | null {
  if (!mesh || mesh.vertices.length === 0) return null
  const n = mesh.vertices.length
  const sum: [number, number, number] = [0, 0, 0]
  for (const v of mesh.vertices) {
    sum[0] += v[0]
    sum[1] += v[1]
    sum[2] += v[2]
  }
  return [sum[0] / n, sum[1] / n, sum[2] / n]
}

export interface ProjectShellRenderEntry {
  projectId: string
  /** SAME palette entry projectTagColor gives the 2-D Atlas for this id —
   * one colour per project, everywhere in the app. */
  color: [number, number, number]
  tight: ShellMesh | null
  loose: ShellMesh | null
  /** The tight shell's own centroid when one exists, else the loose
   * shell's — never fabricated when neither exists (label simply omitted
   * by the caller). */
  centroid: [number, number, number] | null
}

/** `/portfolio_shells`'s `shells_by_project` -> one render entry per
 * project, pre-joined with its app-theme colour and label anchor. Pure
 * mapping — Explorer3D.tsx's mount/retarget effects turn each entry into
 * two THREE.Mesh objects (tight/loose) + wireframe LineSegments + a DOM
 * label, never re-deriving the colour or centroid math inline. */
export function buildProjectShellRenderEntries(
  shellsByProject: Record<string, ProjectShells>,
  memberProjectIds: string[],
): ProjectShellRenderEntry[] {
  return Object.entries(shellsByProject).map(([projectId, shells]) => ({
    projectId,
    color: hexToRgb01(projectTagColor(projectId, memberProjectIds)),
    tight: shells.tight,
    loose: shells.loose,
    centroid: shellCentroid(shells.tight) ?? shellCentroid(shells.loose),
  }))
}

/** Does `groups` (the already-fetched portfolio overlap groups list —
 * PortfolioAtlas.tsx/OverlapView.tsx's own `getPortfolioOverlap` query,
 * never a second fetch) contain a REAL formed group for this shell
 * intersection's project pair? Used to decide whether the 3-D Explorer's
 * intersection tooltip can offer a real "Open its overlap group ->" queued
 * drill-in, or must show the honest "no formed group yet" fallback (the
 * pair can geometrically/full-space overlap in 3-D before the SEPARATE
 * overlap-scoring engine has run, or if it scored below the >=40 group-
 * formation threshold — never fabricate a group link that doesn't exist). */
export function findMatchingOverlapGroup<T extends { group_id: string; member_project_ids: string[]; score: number }>(
  projectIds: readonly [string, string],
  groups: T[] | null | undefined,
): T | null {
  if (!groups) return null
  const [a, b] = projectIds
  return groups.find((g) => g.member_project_ids.includes(a) && g.member_project_ids.includes(b)) ?? null
}
